// Retrospecto das últimas 10 partidas, como a coluna "Últimas 5" da tabela do Brasileirão: da mais antiga, à
// esquerda, para a última, à direita. Conta toda partida terminada, em qualquer sala; a anulada não conta. Sair da
// partida conta como derrota; quem caiu e não voltou fica com o resultado do trio.
import type { Team } from '@truco6/regras';
import type { JogadorDaPartida, TipoSala } from './tipos.ts';

export const PARTIDAS_NO_RETROSPECTO = 10;

export type TipoDeSalaNoRetrospecto = 'liga' | 'tentos' | 'campeonato' | 'privada' | 'publica';
export interface PessoaDaPartida { usuario: string; apelido: string; avatar: string | null }
export interface DetalheDaPartida {
  placar: [number, number]; // o trio de quem é o retrospecto primeiro
  sala: string | null; // nome da sala; nulo na privada (aparece só "Sala privada") e na liga
  parceiros: PessoaDaPartida[];
  adversarios: PessoaDaPartida[];
  bots: { parceiros: number; adversarios: number };
}
export interface PartidaDoRetrospecto {
  partida: string;
  terminadaEm: number;
  resultado: 'V' | 'D';
  tipoSala: TipoDeSalaNoRetrospecto;
  detalhe?: DetalheDaPartida;
}

// Sair conta derrota; os outros ficam com o resultado do trio.
export function resultadoNaPartida(jogadores: readonly JogadorDaPartida[], vencedor: Team, usuario: string): 'V' | 'D' | null {
  const j = jogadores.find((x) => x.usuario === usuario);
  if (!j) return null;
  return !j.saiu && j.time === vencedor ? 'V' : 'D';
}

// Sala de amigo sem a informação de privada (partidas antigas) conta como privada: o nome não aparece.
export function tipoDeSalaNoRetrospecto(tipo: TipoSala, privada: boolean | null | undefined): TipoDeSalaNoRetrospecto {
  if (tipo === 'liga' || tipo === 'tentos' || tipo === 'campeonato') return tipo;
  return privada === false ? 'publica' : 'privada';
}

// V, D e quantos quadros ficam vazios (tracejados, à esquerda).
export function contagemDoRetrospecto(lista: readonly { resultado: 'V' | 'D' }[]): { v: number; d: number; vazios: number } {
  const v = lista.filter((x) => x.resultado === 'V').length;
  return { v, d: lista.length - v, vazios: Math.max(0, PARTIDAS_NO_RETROSPECTO - lista.length) };
}

// Leitor de tela: "Últimas 10 partidas de Léo: 7 vitórias e 3 derrotas. Da mais antiga para a última: vitória, ...".
export function textoDoRetrospecto(apelido: string, lista: readonly { resultado: 'V' | 'D' }[]): string {
  if (!lista.length) return `${apelido} ainda não tem partidas.`;
  const { v, d } = contagemDoRetrospecto(lista);
  const n = (x: number, um: string, varios: string) => `${x} ${x === 1 ? um : varios}`;
  const titulo = lista.length === 1 ? `Última partida de ${apelido}` : `Últimas ${lista.length} partidas de ${apelido}`;
  const ordem = lista.map((x) => (x.resultado === 'V' ? 'vitória' : 'derrota')).join(', ');
  return `${titulo}: ${n(v, 'vitória', 'vitórias')} e ${n(d, 'derrota', 'derrotas')}. Da mais antiga para a última: ${ordem}.`;
}

// Uma partida gravada, como as duas versões do armazém leem (memória e partidas_processadas).
export interface PartidaGravada {
  id: string;
  terminadaEm: number;
  tipo: TipoSala;
  privada: boolean | null;
  salaNome: string | null; // só de sala pública
  vencedor: Team;
  placar: [number, number];
  bots: [number, number];
  jogadores: readonly JogadorDaPartida[];
}

// A partida vista por quem é o retrospecto. Com perfil, o detalhe: placar do trio dele primeiro, a sala (sem nome na
// privada), parceiros, adversários e os bots de cada lado.
export function partidaNoRetrospecto(p: PartidaGravada, usuario: string, perfil?: (id: string) => { apelido: string; avatar: string | null }): PartidaDoRetrospecto {
  const tipoSala = tipoDeSalaNoRetrospecto(p.tipo, p.privada);
  const base: PartidaDoRetrospecto = { partida: p.id, terminadaEm: p.terminadaEm, resultado: resultadoNaPartida(p.jogadores, p.vencedor, usuario) ?? 'D', tipoSala };
  if (!perfil) return base;
  const t = (p.jogadores.find((j) => j.usuario === usuario)?.time ?? 0) as Team, o = (1 - t) as Team;
  const pessoa = (j: JogadorDaPartida): PessoaDaPartida => ({ usuario: j.usuario, ...perfil(j.usuario) });
  return {
    ...base,
    detalhe: {
      placar: [p.placar[t], p.placar[o]],
      sala: p.privada === false && tipoSala !== 'liga' ? p.salaNome : null,
      parceiros: p.jogadores.filter((j) => j.time === t && j.usuario !== usuario).map(pessoa),
      adversarios: p.jogadores.filter((j) => j.time === o).map(pessoa),
      bots: { parceiros: p.bots[t], adversarios: p.bots[o] },
    },
  };
}
